import { useEffect, useState } from "react";
import { IAnswer } from "../../../models/Quiz";
import { useSelector } from "react-redux";
import {
  selectReferenceLanguage,
  selectSelectedElement,
  updateSelectedInteraction,
} from "../../../app/slices/TrainingSlice";
import { IWordSpinnerQuiz } from "../../../models/interactions/WordSpinnerQuiz";
import { useTrainingLocalization } from "../../trainingLocalization/TrainingLocalizationHooks";
import { selectCurrentLanguage } from "../../../app/slices/TrainingLocalizationSlice";
import { GetNewGuid } from "../../../helpers/GuidHelper";
import { useDispatch } from "react-redux";
import { arrayMove } from "@dnd-kit/sortable";
import { useWordSpinnerLocalizationHelper } from "./WordSpinnerLocalizationHelper";

export default function useWordSpinnerQuizBody() {
  const dispatch = useDispatch();
  const quiz = useSelector(selectSelectedElement) as IWordSpinnerQuiz;
  const refLang = useSelector(selectReferenceLanguage);
  const currLang = useSelector(selectCurrentLanguage);
  const { getValue, changeValue, getNewKey } = useTrainingLocalization();
  const { removeBracketsFromAllLanguages } = useWordSpinnerLocalizationHelper();

  const [chunks, setChunks] = useState<IAnswer[]>([]);
  const [editedSentence, setEditedSentence] = useState<string>("");
  const [sentenceIndices, setSentenceIndices] = useState<number[]>([]);
  const [isEditingSentence, setIsEditingSentence] = useState<boolean>(false);
  const [isSentenceChanged, setIsSentenceChanged] = useState<boolean>(false);

  // this is questionable
  const reformatSentence = () => {
    changeValue(
      quiz.formattedSentence,
      editedSentence.replace(/(?<=\{)[^{}]*(?=\})/, "0")
    );
  };

  const getRawSentence = (): string => {
    var sentence: string = getValue(quiz.formattedSentence);
    const correctAnswer = quiz.answers.find((answer) => answer.correct);

    if (correctAnswer) {
      sentence = sentence.replace("{0}", `{${getValue(correctAnswer.text)}}`);
    }

    return sentence;
  };

  // --- use effects ---
  // --- when stop editing the sentence ---
  useEffect(() => {
    if (!isEditingSentence && isSentenceChanged) {
      setIsSentenceChanged(false);
      findCorrectAnswer();
    }
  }, [isEditingSentence]);

  useEffect(() => {
    setEditedSentence(getRawSentence());
  }, [quiz.id, currLang]);

  useEffect(() => {
    generateChunks();
    reformatSentence();
  }, [editedSentence]);

  const generateChunks = () => {
    var sentence = editedSentence;

    // Use a regular expression with capturing groups to split and capture the parts
    // this can only be split if it has a correct answer, right?
    const partsWithBraces: string[] = sentence.split(/(\{[^{}]*\})/);
    const newChunks: IAnswer[] = [];

    for (var i = 0; i < partsWithBraces.length; i++) {
      if (i % 2 != 0) {
        var answer: IAnswer = {
          id: GetNewGuid(),
          correct: false,
          text: partsWithBraces[i],
        };

        answer.correct = true;
        newChunks.push(answer);
      } else {
        // split that text
        var txt = partsWithBraces[i].split(" ");
        txt.forEach((str) => {
          if (str !== "") {
            newChunks.push({
              id: GetNewGuid(),
              correct: false,
              text: str,
            });
          }
        });
      }
    }

    setChunks(newChunks);
  };

  // --- registering a correct answer ---
  // TODO: check this !
  const registerCorrectAnswer = () => {
    var split = chunks.map((chunk) => chunk.text.replace(/[{}]/g, ""));

    // get smallest index
    // get biggest index
    var si = Math.min(...sentenceIndices);
    var sb = Math.max(...sentenceIndices);

    const correctAnswer: string = split.slice(si, sb + 1).join(" ");
    const correctAnswerExists = quiz.answers.find((a) => a.correct);

    split[si] = "{" + split[si];
    split[sb] = split[sb] + "}";

    setEditedSentence(split.join(" "));
    setSentenceIndices([]);

    if (!correctAnswerExists) addCorrectAnswer(correctAnswer);
    else {
      const answers = [...quiz.answers];
      answers.forEach((answer, index) =>
        answer.correct ? changeValue(answers[index].text, correctAnswer) : ""
      );

      const copy: IWordSpinnerQuiz = { ...quiz, answers: answers };
      dispatch(updateSelectedInteraction(copy));
    }
  };

  const setAllAnswersToFalse = () => {
    let answers: IAnswer[] = [...quiz.answers];
    answers = answers.map((answer) => {
      return {
        ...answer,
        correct: false,
      };
    });

    dispatch(
      updateSelectedInteraction({
        ...quiz,
        answers: answers,
      } as IWordSpinnerQuiz)
    );
  };

  // --- when stop editing the sentence ---
  const findCorrectAnswer = () => {
    //Is there a match after editing the sentence?
    const match = editedSentence.match(/\{(.*?)\}/);
    if (!match) {
      const correctAnswer = quiz.answers.find((a) => a.correct);

      // if curr language is lang ref
      if (currLang.shortcode === refLang) {
        // removeBracketsFromAllLanguages(
        //   quiz.formattedSentence,
        //   correctAnswer?.text
        // );
      }
      // setAllAnswersToFalse();

      return;
    }

    const correctAnswerExists = quiz.answers.find((a) => a.correct);
    if (!correctAnswerExists) addCorrectAnswer(match[1]);
    else {
      const answers = [...quiz.answers];
      answers.forEach((answer, index) =>
        answer.correct ? changeValue(answers[index].text, match[1]) : ""
      );

      const copy: IWordSpinnerQuiz = { ...quiz, answers: answers };
      dispatch(updateSelectedInteraction(copy));
    }

    const firstOpenBraceIndex = editedSentence.indexOf("{");
    const firstCloseBraceIndex = editedSentence.indexOf("}");

    var editedSentenceLocal = editedSentence;

    // Replace all curly braces with empty string
    editedSentenceLocal = editedSentenceLocal.replace(/{|}/g, "");

    const sentencePart1 = editedSentenceLocal.slice(0, firstOpenBraceIndex);
    const sentencePart2 = editedSentenceLocal.slice(firstCloseBraceIndex - 1);

    // Add the first pair of curly braces back
    editedSentenceLocal =
      sentencePart1 +
      "{" +
      editedSentenceLocal.slice(firstOpenBraceIndex, firstCloseBraceIndex - 1) +
      "}" +
      sentencePart2;

    setEditedSentence(editedSentenceLocal);
  };

  // --- changing answers ---
  const onChangeAnswer = (
    text: string,
    answer: IAnswer,
    isCorrect: boolean
  ) => {
    if (isCorrect) {
      var chunkref = chunks.find((c) => c.correct);
      var ci = chunks.indexOf(chunkref!);
      var chunksCopy = [...chunks];
      chunksCopy[ci] = { ...chunksCopy[ci], text: "{" + text + "}" };
      setChunks(chunksCopy);

      // also update source sentenceNO,
      var formatted = chunksCopy.map((c) => c.text).join(" ");
      setEditedSentence(formatted);
    }

    changeValue(answer.text, text);
  };

  // --- deleting answers ---
  const onDeleteAnswer = (answer: IAnswer) => {
    if (answer.correct) {
      var chunkref = chunks.find((c) => c.correct);
      var ci = chunks.indexOf(chunkref!);
      var chunksCopy = [...chunks];
      chunksCopy[ci] = {
        ...chunksCopy[ci],
        correct: false,
        text: chunksCopy[ci].text.replace("{", "").replace("}", ""),
      };
      setChunks(chunksCopy);

      // also update source sentence
      var formatted = chunksCopy.map((c) => c.text).join(" ");
      setEditedSentence(formatted);

      // apply to all languages
      removeBracketsFromAllLanguages(quiz.formattedSentence, answer.text);
    }

    deleteAnswer(answer.id);
  };

  // --- change content editable ---
  const onChangeContentEditable = (newValue: string) => {
    setIsSentenceChanged(true);
    setEditedSentence(newValue);
  };

  const onBlurContentEditable = (innerText: string) => {
    setIsSentenceChanged(true);
    setEditedSentence(innerText);
    setIsEditingSentence(false);
  };

  // --- slice helpers ---
  const addCorrectAnswer = (answerText: string) => {
    var answer: IAnswer = {
      id: GetNewGuid(),
      correct: true,
      text: getNewKey(),
    };
    changeValue(answer.text, answerText);

    const answers = [...quiz.answers];
    answers.push(answer);

    const copy: IWordSpinnerQuiz = { ...quiz, answers: answers };
    dispatch(updateSelectedInteraction(copy));
  };

  const deleteAnswer = (id: string) => {
    var filteredAnswers = quiz.answers.filter((a) => a.id !== id);
    const copy: IWordSpinnerQuiz = { ...quiz, answers: filteredAnswers };
    dispatch(updateSelectedInteraction(copy));
  };

  const addWrongAnswer = () => {
    const allowNewAnswer: boolean =
      quiz.answers.findIndex((answer) => answer.correct) === -1
        ? quiz.answers.length < 5
        : quiz.answers.length < 6;
    if (!allowNewAnswer) return;

    var answer: IAnswer = {
      id: GetNewGuid(),
      correct: false,
      text: getNewKey(),
    };

    const answers = [...quiz.answers];
    answers.push(answer);

    const copy: IWordSpinnerQuiz = { ...quiz, answers: answers };
    dispatch(updateSelectedInteraction(copy));
  };

  const onToggleCorrectAnswer = (currentIndex: number) => {
    var answers = [...quiz.answers];
    answers = answers.map((answer, mappingIndex) => {
      return {
        ...answer,
        correct: mappingIndex === currentIndex,
      };
    });

    var correctAnswerText = getValue(answers[currentIndex].text);

    var updatedSentence = editedSentence.replace(
      /{(.*?)}/g,
      "{" + correctAnswerText + "}"
    );
    setEditedSentence(updatedSentence);

    const copy: IWordSpinnerQuiz = { ...quiz, answers: answers };
    dispatch(updateSelectedInteraction(copy));
  };

  // --- drag logic ---
  // The indices are positions in the mapped answers, which can hide the correct one
  function onHandleDragEnd(prevIndex: number, newIndex: number) {
    const mapped = getMappedAnswers();
    const from = quiz.answers.indexOf(mapped[prevIndex]);
    const to = quiz.answers.indexOf(mapped[newIndex]);
    if (from < 0 || to < 0) return;

    var copy = [...quiz.answers];
    copy = arrayMove(copy, from, to);

    var updatedQuiz = { ...quiz };
    updatedQuiz.answers = copy;

    dispatch(updateSelectedInteraction(updatedQuiz));
  }

  // --- mapped answers ---
  function getMappedAnswers() {
    const sentence = getValue(quiz.formattedSentence);
    if (sentence.includes("{0}")) return quiz.answers;

    return quiz.answers.filter((a) => !a.correct);
  }

  return {
    chunks,
    editedSentence,
    setEditedSentence,
    sentenceIndices,
    setSentenceIndices,
    onChangeAnswer,
    onDeleteAnswer,
    generateChunks,
    registerCorrectAnswer,
    addWrongAnswer,
    findCorrectAnswer,
    onToggleCorrectAnswer,
    isEditingSentence,
    setIsEditingSentence,
    onChangeContentEditable,
    onBlurContentEditable,
    onHandleDragEnd,
    getMappedAnswers,
  } as const;
}
